/**
 * Equipment Entities - Betrieb Müller
 *
 * Farm equipment as Digital Twin entities.
 */

import type { EquipmentEntity } from '@hofsync/design-system';
import { daysAgo, daysFromNow, yearsAgo } from '../farm-profile';

/**
 * Güllefass - Slurry tanker with expiring TÜV
 */
export const EQUIPMENT_GUELLEFASS: EquipmentEntity = {
  id: 'equipment-guellefass',
  type: 'equipment',
  name: 'Güllefass Kotte VT 18.000',
  status: 'warning',
  lastUpdated: daysAgo(5),
  properties: {
    equipment_category: 'transport',
    equipmentType: 'Güllefass',
    manufacturer: 'Kotte',
    model: 'VT 18.000',
    year: 2018,
    licensePlate: 'ROW-KM 42',
    nextInspection: daysFromNow(42), // TÜV in 6 weeks
    capacity: '18.000 Liter',
  },
  documents: [
    {
      id: 'doc-tuev-guellefass-2024',
      name: 'TÜV-Bericht Güllefass',
      type: 'zertifikat',
      date: yearsAgo(2),
      source: 'TÜV Nord',
      status: 'expiring',
      expiresAt: daysFromNow(42),
      referenceNumber: 'HU-2024-034567',
      extractedData: {
        ergebnis: 'bestanden',
        mängel: 'keine',
        nächsteHU: daysFromNow(42).toISOString().split('T')[0],
      },
      relatedEntityIds: ['equipment-guellefass'],
      satisfiesRequirements: ['stvzo-29'],
    },
  ],
};

/**
 * Traktor - Main tractor with valid TÜV
 */
export const EQUIPMENT_TRAKTOR: EquipmentEntity = {
  id: 'equipment-traktor',
  type: 'equipment',
  name: 'Fendt 724 Vario',
  status: 'active',
  lastUpdated: daysAgo(10),
  properties: {
    equipment_category: 'tractors',
    equipmentType: 'Traktor',
    manufacturer: 'Fendt',
    model: '724 Vario',
    year: 2020,
    licensePlate: 'ROW-KM 1',
    nextInspection: daysFromNow(280), // TÜV in ~9 months
    capacity: '240 PS',
  },
  documents: [
    {
      id: 'doc-tuev-traktor-2024',
      name: 'TÜV-Bericht Traktor',
      type: 'zertifikat',
      date: yearsAgo(1),
      source: 'TÜV Nord',
      status: 'valid',
      expiresAt: daysFromNow(280),
      referenceNumber: 'HU-2024-034123',
      extractedData: {
        ergebnis: 'bestanden',
        mängel: 'keine',
        nächsteHU: daysFromNow(280).toISOString().split('T')[0],
      },
      relatedEntityIds: ['equipment-traktor'],
      satisfiesRequirements: ['stvzo-29'],
    },
  ],
};

/**
 * Feldspritze - Field sprayer with valid inspection
 */
export const EQUIPMENT_FELDSPRITZE: EquipmentEntity = {
  id: 'equipment-feldspritze',
  type: 'equipment',
  name: 'Amazone UX 4200',
  status: 'active',
  lastUpdated: daysAgo(30),
  properties: {
    equipment_category: 'sprayers',
    equipmentType: 'Feldspritze',
    manufacturer: 'Amazone',
    model: 'UX 4200',
    year: 2019,
    nextInspection: daysFromNow(500), // Next check in ~16 months
    capacity: '4.200 Liter',
  },
  documents: [
    {
      id: 'doc-spritzentuev-2023',
      name: 'Spritzen-TÜV Prüfbericht',
      type: 'zertifikat',
      date: yearsAgo(2.5),
      source: 'Landmaschinentechnik Meyer',
      status: 'valid',
      expiresAt: daysFromNow(500),
      referenceNumber: 'SPT-2023-0456',
      extractedData: {
        ergebnis: 'bestanden',
        querverteilung: 'innerhalb Toleranz',
        düsenprüfung: 'bestanden',
      },
      relatedEntityIds: ['equipment-feldspritze'],
      satisfiesRequirements: ['pflschg-11'],
    },
  ],
};

/**
 * Anhänger - Trailer with valid TÜV
 */
export const EQUIPMENT_ANHAENGER: EquipmentEntity = {
  id: 'equipment-anhaenger',
  type: 'equipment',
  name: 'Krampe Big Body 750',
  status: 'active',
  lastUpdated: daysAgo(15),
  properties: {
    equipment_category: 'transport',
    equipmentType: 'Kipper',
    manufacturer: 'Krampe',
    model: 'Big Body 750',
    year: 2017,
    licensePlate: 'ROW-KM 3',
    nextInspection: daysFromNow(180), // TÜV in ~6 months
    capacity: '22 m³',
  },
  documents: [
    {
      id: 'doc-tuev-anhaenger-2025',
      name: 'TÜV-Bericht Anhänger',
      type: 'zertifikat',
      date: daysAgo(180),
      source: 'TÜV Nord',
      status: 'valid',
      expiresAt: daysFromNow(180),
      referenceNumber: 'HU-2025-034890',
      extractedData: {
        ergebnis: 'bestanden',
        mängel: 'keine',
        nächsteHU: daysFromNow(180).toISOString().split('T')[0],
      },
      relatedEntityIds: ['equipment-anhaenger'],
      satisfiesRequirements: ['stvzo-29'],
    },
  ],
};

/**
 * All equipment entities
 */
export const ALL_EQUIPMENT: EquipmentEntity[] = [
  EQUIPMENT_GUELLEFASS,
  EQUIPMENT_TRAKTOR,
  EQUIPMENT_FELDSPRITZE,
  EQUIPMENT_ANHAENGER,
];

/**
 * Get equipment by ID
 */
export function getEquipmentById(id: string): EquipmentEntity | undefined {
  return ALL_EQUIPMENT.find((eq) => eq.id === id);
}

/**
 * Get equipment with expiring inspections (within next 60 days)
 */
export function getEquipmentWithExpiringInspection(): EquipmentEntity[] {
  return ALL_EQUIPMENT.filter((eq) => eq.status === 'warning');
}
